import {
    Button,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import useRequest from '../../hooks/useRequest';
import type { SalaryEmployee, SalaryPayrollResponse } from '../../domain/salary/types';

interface Props {
    employees: SalaryEmployee[];
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
}

const today = new Date();

const PayrollHeaderCell = ({ label, hint }: { label: string; hint: string }) => (
    <TableCell align="right">
        {label}
        <InfoHint title={hint} />
    </TableCell>
);

const PayrollTab = ({ employees }: Props) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [result, setResult] = useState<SalaryPayrollResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const employeeName = useCallback(
        (id: number) => employees.find(e => e.id === id)?.name ?? `#${id}`,
        [employees],
    );

    const handleCompute = async () => {
        setLoading(true);
        try {
            const response = await request<ApiResponse<SalaryPayrollResponse>>({
                path: '/salary/payroll',
                params: { year, month },
            });
            setResult(response.data);
        } finally {
            setLoading(false);
        }
    };

    const handlePersist = async () => {
        await request({
            path: '/salary/payroll/persist',
            method: 'POST',
            params: { year, month },
        });
    };

    const formatCurrency = (value: number) =>
        `${Math.round(value).toLocaleString()} ${t('salary.currency')}`;

    return (
        <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
                    <TextField
                        size="small"
                        type="number"
                        label={t('salary.common.year')}
                        value={year}
                        onChange={e => setYear(Number(e.target.value))}
                    />
                    <TextField
                        size="small"
                        type="number"
                        label={t('salary.common.month')}
                        value={month}
                        onChange={e => setMonth(Number(e.target.value))}
                        slotProps={{ htmlInput: { min: 1, max: 12 } }}
                    />
                    <Button variant="contained" onClick={handleCompute} disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : t('salary.payroll.compute')}
                    </Button>
                    <InfoHint title={t('salary.info.payroll.compute')} />
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={handlePersist}
                        disabled={!result}
                    >
                        {t('salary.payroll.persist')}
                    </Button>
                    <InfoHint title={t('salary.info.payroll.persist')} />
                </Stack>
            </Paper>

            {result && (
                <Paper>
                    <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
                        <Typography variant="h6">
                            {t('salary.payroll.headerFor', {
                                year: result.year,
                                month: result.month,
                            })}
                        </Typography>
                        <Stack direction="row" spacing={3}>
                            <Typography>
                                {t('salary.payroll.gross')}:{' '}
                                <strong>{formatCurrency(result.totals.gross)}</strong>
                            </Typography>
                            <Typography>
                                {t('salary.payroll.taxes')}:{' '}
                                <strong>{formatCurrency(result.totals.taxes)}</strong>
                            </Typography>
                            <Typography>
                                {t('salary.payroll.net')}:{' '}
                                <strong>{formatCurrency(result.totals.net)}</strong>
                            </Typography>
                        </Stack>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('salary.schedule.employee')}</TableCell>
                                <PayrollHeaderCell
                                    label={t('salary.payroll.totalHours')}
                                    hint={t('salary.info.payroll.totalHours')}
                                />
                                <PayrollHeaderCell
                                    label={t('salary.payroll.weekendHours')}
                                    hint={t('salary.info.payroll.weekendHours')}
                                />
                                <PayrollHeaderCell
                                    label={t('salary.payroll.holidayHours')}
                                    hint={t('salary.info.payroll.holidayHours')}
                                />
                                <PayrollHeaderCell
                                    label={t('salary.payroll.nightHours')}
                                    hint={t('salary.info.payroll.nightHours')}
                                />
                                <PayrollHeaderCell
                                    label={t('salary.payroll.basePay')}
                                    hint={t('salary.info.payroll.basePay')}
                                />
                                <TableCell align="right">{t('salary.payroll.gross')}</TableCell>
                                <PayrollHeaderCell
                                    label={t('salary.payroll.taxes')}
                                    hint={t('salary.info.payroll.taxes')}
                                />
                                <TableCell align="right">{t('salary.payroll.net')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {result.lines.map(line => (
                                <TableRow key={line.employeeId}>
                                    <TableCell>{employeeName(line.employeeId)}</TableCell>
                                    <TableCell align="right">
                                        {line.totalHours.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {line.weekendHours.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {line.holidayHours.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {line.nightHours.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(line.basePay)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(line.totalGross)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(line.taxes)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency(line.totalNet)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Stack>
    );
};

export default PayrollTab;
